/**
 * Investor Admin Page
 * 
 * Admin-only page for managing investors, creating portfolios,
 * generating payment schedules, and recording received payments.
 * Accessible only to authenticated admin users via the main dashboard.
 */
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import {
  Users,
  Plus,
  Briefcase,
  DollarSign,
  Calendar,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";

const BRAND = {
  bg: "#080d16",
  card: "#0e1623",
  cardAlt: "#0b1220",
  border: "#1a2540",
  accent: "#3b82f6",
  accentLight: "rgba(59,130,246,0.1)",
  gold: "#f59e0b",
  goldLight: "rgba(245,158,11,0.1)",
  green: "#10b981",
  greenLight: "rgba(16,185,129,0.1)",
  red: "#ef4444",
  redLight: "rgba(239,68,68,0.1)",
  text: "#f1f5f9",
  textMuted: "#94a3b8",
  textDim: "#64748b",
};

function FormField({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  required,
}: {
  label: string;
  type?: string;
  value: string | number;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label
        style={{
          display: "block",
          color: BRAND.textMuted,
          fontSize: 12,
          fontWeight: 500,
          marginBottom: 6,
        }}
      >
        {label} {required && <span style={{ color: BRAND.red }}>*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        style={{
          width: "100%",
          background: "rgba(255,255,255,0.04)",
          border: `1px solid ${BRAND.border}`,
          borderRadius: 8,
          padding: "10px 12px",
          color: BRAND.text,
          fontSize: 13,
          outline: "none",
          boxSizing: "border-box",
        }}
        onFocus={(e) => (e.target.style.borderColor = BRAND.accent)}
        onBlur={(e) => (e.target.style.borderColor = BRAND.border)}
      />
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  iconColor,
  children,
  defaultOpen = false,
}: {
  title: string;
  icon: React.ComponentType<{ size?: number; color?: string }>;
  iconColor: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      style={{
        background: BRAND.card,
        border: `1px solid ${BRAND.border}`,
        borderRadius: 14,
        overflow: "hidden",
        marginBottom: 16,
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          borderBottom: open ? `1px solid ${BRAND.border}` : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: iconColor + "18",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon size={15} color={iconColor} />
          </div>
          <span style={{ color: BRAND.text, fontSize: 14, fontWeight: 700 }}>{title}</span>
        </div>
        {open ? (
          <ChevronUp size={16} color={BRAND.textDim} />
        ) : (
          <ChevronDown size={16} color={BRAND.textDim} />
        )}
      </button>
      {open && <div style={{ padding: "20px" }}>{children}</div>}
    </div>
  );
}

function CreateInvestorForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = trpc.investor.createInvestor.useMutation({
    onSuccess: () => {
      setSuccess(true);
      setEmail("");
      setPassword("");
      setName("");
      setCompany("");
      setTimeout(() => setSuccess(false), 4000);
    },
    onError: (err) => setError(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate({ email, password, name, company: company || undefined });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <FormField label="Full Name" value={name} onChange={setName} placeholder="John Smith" required />
        <FormField label="Company" value={company} onChange={setCompany} placeholder="Smith Capital LLC" />
        <FormField label="Email Address" type="email" value={email} onChange={setEmail} placeholder="john@example.com" required />
        <FormField label="Initial Password" type="password" value={password} onChange={setPassword} placeholder="Min. 8 characters" required />
      </div>

      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: BRAND.redLight,
            border: `1px solid ${BRAND.red}40`,
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 14,
          }}
        >
          <AlertCircle size={14} color={BRAND.red} />
          <span style={{ color: BRAND.red, fontSize: 13 }}>{error}</span>
        </div>
      )}

      {success && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: BRAND.greenLight,
            border: `1px solid ${BRAND.green}40`,
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 14,
          }}
        >
          <CheckCircle2 size={14} color={BRAND.green} />
          <span style={{ color: BRAND.green, fontSize: 13 }}>Investor account created successfully.</span>
        </div>
      )}

      <button
        type="submit"
        disabled={mutation.isPending}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: BRAND.accent,
          border: "none",
          borderRadius: 8,
          padding: "10px 18px",
          color: "white",
          fontSize: 13,
          fontWeight: 600,
          cursor: mutation.isPending ? "not-allowed" : "pointer",
          opacity: mutation.isPending ? 0.7 : 1,
        }}
      >
        {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
        Create Investor Account
      </button>
    </form>
  );
}

function CreatePortfolioForm() {
  const investorsQuery = trpc.investor.listInvestors.useQuery();
  const [investorId, setInvestorId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [totalSalePrice, setTotalSalePrice] = useState("");
  const [finalPaymentDate, setFinalPaymentDate] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = trpc.investor.createPortfolio.useMutation({
    onSuccess: () => {
      setSuccess(true);
      setName("");
      setDescription("");
      setPurchaseDate("");
      setPurchasePrice("");
      setTotalSalePrice("");
      setFinalPaymentDate("");
      setTimeout(() => setSuccess(false), 4000);
    },
    onError: (err) => setError(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      investorId: parseInt(investorId),
      name,
      description: description || undefined,
      purchaseDate,
      purchasePrice: parseFloat(purchasePrice),
      totalSalePrice: parseFloat(totalSalePrice),
      finalPaymentDate: finalPaymentDate || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: "block", color: BRAND.textMuted, fontSize: 12, fontWeight: 500, marginBottom: 6 }}>
          Investor <span style={{ color: BRAND.red }}>*</span>
        </label>
        <select
          value={investorId}
          onChange={(e) => setInvestorId(e.target.value)}
          required
          style={{
            width: "100%",
            background: "rgba(255,255,255,0.04)",
            border: `1px solid ${BRAND.border}`,
            borderRadius: 8,
            padding: "10px 12px",
            color: BRAND.text,
            fontSize: 13,
            outline: "none",
          }}
        >
          <option value="">Select investor...</option>
          {(investorsQuery.data || []).map((inv: any) => (
            <option key={inv.id} value={inv.id}>
              {inv.name} ({inv.email})
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <FormField label="Portfolio Name" value={name} onChange={setName} placeholder="Heights Finance Portfolio 1" required />
        <FormField label="Description" value={description} onChange={setDescription} placeholder="Optional description" />
        <FormField label="Purchase Date" type="date" value={purchaseDate} onChange={setPurchaseDate} required />
        <FormField label="Final Payment Date" type="date" value={finalPaymentDate} onChange={setFinalPaymentDate} />
        <FormField label="Purchase Price ($)" type="number" value={purchasePrice} onChange={setPurchasePrice} placeholder="50000" required />
        <FormField label="Total Sale Price / Face Value ($)" type="number" value={totalSalePrice} onChange={setTotalSalePrice} placeholder="500000" required />
      </div>

      {error && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: BRAND.redLight, border: `1px solid ${BRAND.red}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
          <AlertCircle size={14} color={BRAND.red} />
          <span style={{ color: BRAND.red, fontSize: 13 }}>{error}</span>
        </div>
      )}
      {success && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: BRAND.greenLight, border: `1px solid ${BRAND.green}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
          <CheckCircle2 size={14} color={BRAND.green} />
          <span style={{ color: BRAND.green, fontSize: 13 }}>Portfolio created successfully.</span>
        </div>
      )}

      <button
        type="submit"
        disabled={mutation.isPending}
        style={{ display: "flex", alignItems: "center", gap: 8, background: BRAND.accent, border: "none", borderRadius: 8, padding: "10px 18px", color: "white", fontSize: 13, fontWeight: 600, cursor: mutation.isPending ? "not-allowed" : "pointer", opacity: mutation.isPending ? 0.7 : 1 }}
      >
        {mutation.isPending ? <Loader2 size={14} /> : <Plus size={14} />}
        Create Portfolio
      </button>
    </form>
  );
}

function RecordPaymentForm() {
  const investorsQuery = trpc.investor.listInvestors.useQuery();
  const [investorId, setInvestorId] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split("T")[0]);
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = trpc.investor.recordPaymentReceived.useMutation({
    onSuccess: () => {
      setSuccess(true);
      setAmount("");
      setNotes("");
      setTimeout(() => setSuccess(false), 4000);
    },
    onError: (err) => setError(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      investorId: parseInt(investorId),
      paymentDate,
      amount: parseFloat(amount),
      notes: notes || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: "block", color: BRAND.textMuted, fontSize: 12, fontWeight: 500, marginBottom: 6 }}>
          Investor <span style={{ color: BRAND.red }}>*</span>
        </label>
        <select
          value={investorId}
          onChange={(e) => setInvestorId(e.target.value)}
          required
          style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: `1px solid ${BRAND.border}`, borderRadius: 8, padding: "10px 12px", color: BRAND.text, fontSize: 13, outline: "none" }}
        >
          <option value="">Select investor...</option>
          {(investorsQuery.data || []).map((inv: any) => (
            <option key={inv.id} value={inv.id}>
              {inv.name} ({inv.email})
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <FormField label="Payment Date" type="date" value={paymentDate} onChange={setPaymentDate} required />
        <FormField label="Amount ($)" type="number" value={amount} onChange={setAmount} placeholder="5000.00" required />
      </div>
      <FormField label="Notes (optional)" value={notes} onChange={setNotes} placeholder="Weekly payment for Portfolio 1" />

      {error && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: BRAND.redLight, border: `1px solid ${BRAND.red}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
          <AlertCircle size={14} color={BRAND.red} />
          <span style={{ color: BRAND.red, fontSize: 13 }}>{error}</span>
        </div>
      )}
      {success && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: BRAND.greenLight, border: `1px solid ${BRAND.green}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
          <CheckCircle2 size={14} color={BRAND.green} />
          <span style={{ color: BRAND.green, fontSize: 13 }}>Payment recorded successfully.</span>
        </div>
      )}

      <button
        type="submit"
        disabled={mutation.isPending}
        style={{ display: "flex", alignItems: "center", gap: 8, background: BRAND.green, border: "none", borderRadius: 8, padding: "10px 18px", color: "white", fontSize: 13, fontWeight: 600, cursor: mutation.isPending ? "not-allowed" : "pointer", opacity: mutation.isPending ? 0.7 : 1 }}
      >
        {mutation.isPending ? <Loader2 size={14} /> : <DollarSign size={14} />}
        Record Payment
      </button>
    </form>
  );
}

function InvestorList() {
  const investorsQuery = trpc.investor.listInvestors.useQuery();
  const investors = investorsQuery.data || [];

  return (
    <div>
      {investorsQuery.isLoading ? (
        <div style={{ color: BRAND.textMuted, fontSize: 13 }}>Loading investors...</div>
      ) : investors.length === 0 ? (
        <div style={{ color: BRAND.textDim, fontSize: 13 }}>No investors yet. Create one above.</div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {investors.map((inv: any) => (
            <div
              key={inv.id}
              style={{
                background: BRAND.cardAlt,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 10,
                padding: "12px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div style={{ color: BRAND.text, fontSize: 13, fontWeight: 600 }}>{inv.name}</div>
                <div style={{ color: BRAND.textDim, fontSize: 12 }}>{inv.email}</div>
                {inv.company && (
                  <div style={{ color: BRAND.textDim, fontSize: 12 }}>{inv.company}</div>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <span
                  style={{
                    background: inv.is_active ? BRAND.greenLight : BRAND.redLight,
                    color: inv.is_active ? BRAND.green : BRAND.red,
                    padding: "2px 8px",
                    borderRadius: 20,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {inv.is_active ? "Active" : "Inactive"}
                </span>
                {inv.last_login && (
                  <div style={{ color: BRAND.textDim, fontSize: 11, marginTop: 4 }}>
                    Last login:{" "}
                    {new Date(inv.last_login).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function InvestorAdmin() {
  return (
    <div style={{ padding: "32px 36px", maxWidth: 900 }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ color: BRAND.text, fontSize: 24, fontWeight: 700, margin: "0 0 6px 0" }}>
          Investor Management
        </h1>
        <p style={{ color: BRAND.textMuted, fontSize: 14, margin: 0 }}>
          Create investor accounts, assign portfolios, and record payments.
        </p>
      </div>

      <Section title="Investor Accounts" icon={Users} iconColor={BRAND.accent} defaultOpen={true}>
        <InvestorList />
      </Section>

      <Section title="Create Investor Account" icon={Plus} iconColor={BRAND.green} defaultOpen={false}>
        <CreateInvestorForm />
      </Section>

      <Section title="Add Portfolio to Investor" icon={Briefcase} iconColor={BRAND.gold} defaultOpen={false}>
        <CreatePortfolioForm />
      </Section>

      <Section title="Record Payment Received" icon={DollarSign} iconColor={BRAND.green} defaultOpen={false}>
        <RecordPaymentForm />
      </Section>
    </div>
  );
}
